import React from 'react';
import en from './translations/en';
import ta from './translations/ta';

export type SupportedLocale = 'en' | 'ta';

type TranslationMap = Record<string, string>;

type I18nContextValue = {
  locale: SupportedLocale;
  setLocale: (_locale: SupportedLocale) => void;
  t: (_key: string, _replacements?: Record<string, string | number>) => string;
  formatDate: (
    _value: Date | string | number,
    _options?: Intl.DateTimeFormatOptions
  ) => string;
  formatNumber: (_value: number, _options?: Intl.NumberFormatOptions) => string;
};

const STORAGE_KEY = 'app:locale';
const dictionaries: Record<SupportedLocale, TranslationMap> = {
  en,
  ta,
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

function resolveInitialLocale(): SupportedLocale {
  const stored = (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null) || '';
  if (stored === 'ta' || stored === 'en') return stored;
  return 'en';
}

function localeToIntl(locale: SupportedLocale): string {
  return locale === 'ta' ? 'ta-IN' : 'en-IN';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<SupportedLocale>(resolveInitialLocale);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, locale);
    document.documentElement.lang = localeToIntl(locale);
    document.documentElement.dir = 'ltr';
  }, [locale]);

  const t = React.useCallback(
    (key: string, replacements?: Record<string, string | number>) => {
      const dictionary = dictionaries[locale] || dictionaries.en;
      const fallbackDictionary = dictionaries.en;
      let output = dictionary[key] ?? fallbackDictionary[key] ?? key;
      if (replacements) {
        for (const [token, value] of Object.entries(replacements)) {
          output = output.replace(new RegExp(`\\{${token}\\}`, 'g'), String(value));
        }
      }
      return output;
    },
    [locale]
  );

  const formatDate = React.useCallback(
    (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return new Intl.DateTimeFormat(localeToIntl(locale), options).format(date);
    },
    [locale]
  );

  const formatNumber = React.useCallback(
    (value: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(localeToIntl(locale), options).format(value),
    [locale]
  );

  const setLocale = React.useCallback((nextLocale: SupportedLocale) => {
    setLocaleState(nextLocale);
  }, []);

  const value = React.useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      formatDate,
      formatNumber,
    }),
    [formatDate, formatNumber, locale, setLocale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = React.useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return context;
}
